import React, { useState } from "react";
import {
  Box,
  Modal,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OrderEntryCore from "./OrderEntry/OrderEntryCore";
import {
  headerStyle,
  modalStyle,
} from "./OrderEntry/styles";
import { useNavigate } from "react-router-dom";
import { PROTOCOL_TYPES } from "../Constants/AppConstants";
import { tokens } from '../Common/designTokens'; // added token import
import { useActualOrderStore } from "../Store/actualOrderStore"; // new import
import { isLiveOrder } from "../Constants/stagingBlotter";
import { isTradingDeskSelected } from "./OrderEntry/optionsHelper";
import { LIST_RFQ_TAB_INDEX } from "../Store/listRfqStore";

/**
 * Extra modal width, in pixels, added to the order entry modal's long-standing 66vw.
 *
 * One value for ALL four tabs - Bond Order, Bond Characteristic, Bond Ladder and List Order -
 * so the modal does not resize when the trader moves between them. It began as a widening for
 * the List Order tab alone, which holds a bond grid rather than a single form, but a modal
 * that jumped wider on one tab was worse than one that is simply a little roomier throughout.
 */
const ORDER_ENTRY_EXTRA_WIDTH_PX = 100;

const OrderEntry = ({
  readOnly = false,
  open,
  onClose,
  source = "inventory",
  initialActiveTab = 0,
  tabName = "empty",
  token,
  cusip = "",
  side = "",
  qty = "",
  onOrderAdded,
  initialData,
  qtyPriceEditMode,
  secondarySource,
}) => {
  const navigate = useNavigate();
  const [clearAllCallback, setClearAllCallback] = useState(null);
  const { resetActualOrderData } = useActualOrderStore(); // new hook usage

  const handleClose = () => {
    if (clearAllCallback) {
      clearAllCallback();
    }
    resetActualOrderData(); // reset shared store
    onClose();
  };

  // Add effect to cleanup on unmount
  // React.useEffect(() => {
  //   return () => {
  //     if (clearAllCallback) {
  //       clearAllCallback();
  //     }
  //     resetActualOrderData(); // ensure store resets even if modal is unmounted
  //   };
  // }, [clearAllCallback, resetActualOrderData]);

  const handleSubmitSuccess = (success, activeTab, orderData) => {
    let expandOrderId = undefined;
    if (activeTab === 0 && orderData.protocol === PROTOCOL_TYPES.RFQ) {
      expandOrderId = success;
    }

    // A List RFQ goes straight to the venue - there is no staging equivalent - and the
    // orders it creates appear on the Order Blotter like any other live order, one row per
    // bond. So it always lands on view 1, tab 0. Without this the generic path at the end
    // would redirect to view 0 tab 3, and tab 3 only exists inside Order Entry.
    if (activeTab === LIST_RFQ_TAB_INDEX) {
      navigate("/order?view=1&tab=0", { replace: true });
      return;
    }

    // Trading Desk is not a market destination -- the order stays in the Staging Blotter --
    // so always land the trader on it (view 0, tab 0). This has to sit ahead of the
    // source-specific branches below, which redirect to the Order Blotter unconditionally,
    // and ahead of the isLiveOrder() check, which reads the staging flag: a draft raised
    // from Order Entry never carries that flag (only the send-to-market flow stamps it),
    // so Number(undefined) !== 0 made every Trading Desk order look live.
    if (activeTab === 0 && isTradingDeskSelected(orderData.venue)) {
      navigate(`/order?view=0&tab=0${expandOrderId ? `&expandOrderId=${expandOrderId}` : ""}`, {
        replace: true,
      });
      return;
    }

    if (source === "maInventoryAddOrder") {
      navigate(`/order?view=1&tab=0${expandOrderId ? `&expandOrderId=${expandOrderId}` : ""}`, {
        replace: true,
      });
      return;
    }

    if (source === "sendToMarket") {
      navigate(`/order?view=1&tab=0${expandOrderId ? `&expandOrderId=${expandOrderId}` : ""}`);
      return;
    }

    let redirectView = 0;
    let redirectTab = activeTab;

    if (activeTab === 0) {
      const venue = orderData.venue;
      if (venue && isLiveOrder(orderData)) {
        redirectView = 1;
      }
    }

    navigate(`/order?view=${redirectView}&tab=${redirectTab}${expandOrderId ? `&expandOrderId=${expandOrderId}` : ""}`, {
      replace: true,
    });
  };

  // Same width on every tab, so the modal never resizes as the trader switches between them.
  // Held here rather than inside OrderEntryCore because the width belongs to the modal, and
  // it is a widening only - no tab is narrower than the 66vw it used to be.
  const modalWidth = `calc(66vw + ${ORDER_ENTRY_EXTRA_WIDTH_PX}px)`;

  const getModalTitle = () => {
    if (source === "allocateAccount") return "Allocate Account"; // NEW
    if (source === "maInventoryAddOrder") return "Bond Order";
    if (initialData?.editRfqMode) return "Modify MarketAxess RFQ";
    if (secondarySource === "ladder-level-send-to-market") return "Send Ladder Orders to Market";
    if (source === "sendToMarket") return "Send To Market";
    if (source === "orderViaCharacteristic") return "Add Order Associated With Characteristic"; // NEW
    // Ladder opened from a row's Ladder Name / Source column: name the ladder rather
    // than falling through to the generic "Order Entry"
    if (initialData?.viewMode === "ladder" && initialData?.fromSourceColumn) {
      return initialData.nameOfTheLadder
        ? `Bond Ladder - ${initialData.nameOfTheLadder}`
        : "Bond Ladder";
    }
    if (source === "orderBlotter") {
      if (initialActiveTab === 0) {
        return qtyPriceEditMode ? "Modify Order" : "Bond Order";
      }
     return "Order Entry";
    }
    return "Order Entry";
  };

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      disableEscapeKeyDown={true}
      aria-labelledby="order-entry-modal-title"
    >
      <Box sx={{
        ...modalStyle,
        display: "flex",
        flexDirection: "column",
        maxHeight: "calc(95vh + 10px)",
        width: modalWidth,
        maxWidth: "90vw",
        overflow: "hidden",
      }}>
        <Box sx={{
          ...headerStyle,
          flexShrink: 0,
        }}>
          <Typography
            id="order-entry-modal-title"
            variant="h6"
            component="h2"
            sx={{ fontWeight: "bold" }}
          >
            {getModalTitle()}
          </Typography>
          <IconButton onClick={handleClose} size="small" sx={{ color: tokens.colors.fontColour }}>
            <CloseIcon />
          </IconButton>
        </Box>
        
        <OrderEntryCore
          readOnly={readOnly}
          onClose={handleClose}
          source={source}
          secondarySource={secondarySource}
          initialActiveTab={initialActiveTab}
          tabName={tabName}
          cusip={cusip}
          side={side}
          qty={qty}
          onOrderAdded={onOrderAdded}
          initialData={initialData}
          showTabs={source !== "orderViaCharacteristic" && source !== "maInventoryAddOrder"} // NEW: hide tabs for characteristic orders
          showFooter={true}
          onSubmitSuccess={handleSubmitSuccess}
          onClearAll={setClearAllCallback}
          qtyPriceEditMode={qtyPriceEditMode}
          containerStyle={{ 
            flex: 1,
            overflow: "hidden",
            width: "100%",
          }}
        />
      </Box>
    </Modal>
  );
};

export default OrderEntry;
